import { SouscriptionAddon } from './SouscriptionAddon.js';
import { ventesAddonsApi } from '../../lib/addon-ventes.js';
import { modeDemo } from '../../lib/api.js';
import { RemiseAddon } from './RemiseAddon.js';
import catalogue from '../Interrupteurs.module.css';
import formStyles from '../../styles/form.module.css';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Dialog, Input } from '@artosera/ui';
import { CATEGORIES_MODULE, MODES_ADDON, type ModeAddon } from '@artosera/shared';
import { addonsConsoleApi, type AddonGalerieVue } from '../../lib/addons.js';
import { useLibellesModules } from '../../lib/modules.js';
import { usePeut } from '../../auth/usePeut.js';
import type { GalerieFiche } from '../../types.js';
import { FicheFacturation } from './FicheFacturation.js';
import { IaGalerie } from './IaGalerie.js';
import styles from '../../styles/page.module.css';

/** Les mêmes champs servent à l'attribution, la prolongation et la conversion. */
export function AddonsTab({
  idGalerie,
  galerie,
}: {
  idGalerie: string;
  galerie: GalerieFiche;
}): JSX.Element {
  const { t, i18n } = useTranslation('console');
  const libelles = useLibellesModules();
  const peut = usePeut();
  const client = useQueryClient();
  const cle = ['console', 'galerie', idGalerie, 'addons'];
  const requete = useQuery({ queryKey: cle, queryFn: () => addonsConsoleApi.galerie(idGalerie) });
  const souscriptions = useQuery({
    queryKey: ['console', 'galerie', idGalerie, 'souscriptions'],
    queryFn: () => ventesAddonsApi.souscriptions(idGalerie),
    enabled: peut('console:read-addon-sales') && !modeDemo(),
  });
  const [cible, setCible] = useState<{ ligne: AddonGalerieVue; retrait: boolean } | null>(null);
  const [mode, setMode] = useState<ModeAddon>('essai');
  const [depuisLe, setDepuisLe] = useState('');
  const [jusquAu, setJusquAu] = useState('');
  const [motif, setMotif] = useState('');
  const mutation = useMutation({
    mutationFn: async () => {
      if (!cible || !requete.data) return;
      const revisionAddons = requete.data.revisionAddons;
      return cible.retrait
        ? addonsConsoleApi.retirer(idGalerie, cible.ligne.addon, { motif, revisionAddons })
        : addonsConsoleApi.ecrire(idGalerie, cible.ligne.addon, {
            mode,
            depuisLe: new Date(depuisLe).toISOString(),
            jusquAu: jusquAu ? new Date(jusquAu).toISOString() : null,
            motif,
            revisionAddons,
          });
    },
    onSuccess: (donnees) => {
      if (donnees) client.setQueryData(cle, donnees);
      setCible(null);
      void client.invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] });
    },
    onError: () => {
      void client.invalidateQueries({ queryKey: cle });
    },
  });
  function ouvrir(ligne: AddonGalerieVue, retrait: boolean) {
    mutation.reset();
    setCible({ ligne, retrait });
    setMode(ligne.droit?.mode ?? 'essai');
    setDepuisLe((ligne.droit?.depuisLe ?? new Date().toISOString()).slice(0, 10));
    setJusquAu(ligne.droit?.jusquAu?.slice(0, 10) ?? '');
    setMotif('');
  }
  const modifiable =
    peut('console:manage-addons') &&
    !['resiliee', 'purge_en_cours', 'purgee'].includes(galerie.etat);
  const date = (valeur: string) => new Date(valeur).toLocaleDateString(i18n.language);
  return (
    <div className={catalogue.catalogue}>
      <FicheFacturation
        idGalerie={idGalerie}
        fermee={['resiliee', 'purge_en_cours', 'purgee'].includes(galerie.etat)}
      />
      <IaGalerie
        idGalerie={idGalerie}
        fermee={['resiliee', 'purge_en_cours', 'purgee'].includes(galerie.etat)}
      />
      {souscriptions.isError ? <p role="alert">{t('addons.souscriptions.erreur')}</p> : null}
      {requete.isError ? <p role="alert">{t('addons.erreur')}</p> : null}
      {requete.isPending ? <p>{t('addons.charger')}</p> : null}
      {CATEGORIES_MODULE.map((categorie) => {
        const lignes = requete.data?.addons.filter((a) => a.categorie === categorie) ?? [];
        if (!lignes.length) return null;
        return (
          <section className={`${styles.panneau} ${catalogue.catalogue}`} key={categorie}>
            <h2>{t(`interrupteurs.categories.${categorie}`)}</h2>
            {lignes.map((ligne) => (
              <article className={catalogue.module} key={ligne.addon}>
                <h3>{libelles.libelle(ligne.addon)}</h3>
                <p>{libelles.description(ligne.addon)}</p>
                <RemiseAddon
                  galerieId={idGalerie}
                  addon={ligne.addon}
                  remise={requete.data?.remises?.find((r) => r.addon === ligne.addon)}
                />
                {souscriptions.data?.items
                  ?.filter((s) => s.addon === ligne.addon)
                  .map((s) => (
                    <SouscriptionAddon key={s.id} galerieId={idGalerie} souscription={s} />
                  ))}
                <Badge>{t(`addons.etats.${ligne.publication}`)}</Badge>
                {!ligne.disponible ? <Badge>{t('addons.bientot')}</Badge> : null}
                <p>{t(`addons.${ligne.etatDroit}`)}</p>
                {ligne.droit ? (
                  <dl>
                    <dt>{t('addons.mode')}</dt>
                    <dd>{t(`addons.modes.${ligne.droit.mode}`)}</dd>
                    <dt>{t('addons.depuisLe')}</dt>
                    <dd>{date(ligne.droit.depuisLe)}</dd>
                    <dt>{t('addons.jusquAu')}</dt>
                    <dd>
                      {ligne.droit.jusquAu ? date(ligne.droit.jusquAu) : t('addons.sansEcheance')}
                    </dd>
                    <dt>{t('addons.motif')}</dt>
                    <dd>{ligne.droit.motif}</dd>
                    <dt>{t('addons.auteur')}</dt>
                    <dd>{ligne.droit.attribuePar}</dd>
                  </dl>
                ) : null}
                {modifiable ? (
                  <div>
                    {(ligne.droit ? ['prolonger', 'convertir'] : ['attribuer']).map((geste) => (
                      <Button
                        key={geste}
                        disabled={!ligne.disponible}
                        onClick={() => ouvrir(ligne, false)}
                      >
                        {t(`addons.${geste}`)}
                      </Button>
                    ))}
                    {ligne.droit ? (
                      <Button onClick={() => ouvrir(ligne, true)}>{t('addons.retirer')}</Button>
                    ) : null}
                  </div>
                ) : null}
              </article>
            ))}
          </section>
        );
      })}
      <Dialog
        closeLabel={t('addons.annuler')}
        open={cible !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert && !mutation.isPending) setCible(null);
        }}
        title={cible ? libelles.libelle(cible.ligne.addon) : t('addons.titre')}
      >
        <form
          className={formStyles.champ}
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate();
          }}
        >
          {!cible?.retrait ? (
            <>
              <label htmlFor="addon-mode">{t('addons.mode')}</label>
              <select
                id="addon-mode"
                value={mode}
                onChange={(e) => setMode(e.target.value as ModeAddon)}
              >
                {MODES_ADDON.map((m) => (
                  <option key={m} value={m}>
                    {t(`addons.modes.${m}`)}
                  </option>
                ))}
              </select>
              <label htmlFor="addon-debut">{t('addons.depuisLe')}</label>
              <Input
                id="addon-debut"
                type="date"
                required
                value={depuisLe}
                onChange={(e) => setDepuisLe(e.target.value)}
              />
              <label htmlFor="addon-fin">{t('addons.jusquAu')}</label>
              <Input
                id="addon-fin"
                type="date"
                required={mode === 'location' || mode === 'essai'}
                value={jusquAu}
                onChange={(e) => setJusquAu(e.target.value)}
              />
            </>
          ) : null}
          <label htmlFor="addon-motif">{t('addons.motif')}</label>
          <Input
            id="addon-motif"
            required
            maxLength={500}
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
          />
          {mutation.isError ? <p role="alert">{t('addons.echec')}</p> : null}
          <Button type="submit" disabled={mutation.isPending || !motif.trim()}>
            {t('addons.enregistrer')}
          </Button>
          <Button type="button" disabled={mutation.isPending} onClick={() => setCible(null)}>
            {t('addons.annuler')}
          </Button>
        </form>
      </Dialog>
    </div>
  );
}
